
import React from 'react';
import { 
  ArrowLeft, 
  Target, 
  Eye, 
  ShieldCheck, 
  Users, 
  Building2, 
  Globe, 
  Fingerprint, 
  Zap, 
  CheckCircle2,
  Cpu,
  Lock,
  ArrowRight,
  Shield,
  MapPin,
  Compass,
  Award,
  Layers,
  Scale,
  Radar
} from 'lucide-react';

export const AboutView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors duration-500 pb-32">
      {/* Detail Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-100 dark:border-slate-900">
        <div className="max-w-7xl auto px-6 h-20 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center space-x-2 text-slate-500 dark:text-slate-400 font-black text-xs uppercase tracking-widest hover:text-blue-600 dark:hover:text-blue-400 transition-all group">
            <div className="p-2 bg-slate-100 dark:bg-slate-900 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-all"><ArrowLeft size={16} /></div>
            <span>Return Home</span>
          </button>
          <div className="flex items-center space-x-3">
            <img src="assets/logo.png" alt="Minerva Logo" className="w-8 h-8 object-contain" />
            <span className="text-lg font-black tracking-tighter dark:text-white uppercase italic">Mission Control</span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="pt-48 pb-24 px-6 relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center space-y-10 relative z-10">
          <div className="inline-flex items-center space-x-3 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-[10px] font-black uppercase border border-blue-100 dark:border-blue-800/50 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <MapPin size={14} />
            <span>Auckland, New Zealand</span>
          </div>
          <h1 className="text-6xl md:text-9xl font-black text-slate-900 dark:text-white tracking-tighter italic leading-none animate-in fade-in slide-in-from-bottom-4 duration-500">
            Minerva <br /> <span className="text-blue-600">Intelligence.</span>
          </h1>
          <p className="text-xl md:text-3xl text-slate-500 dark:text-slate-400 font-medium leading-relaxed max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-6 duration-500">
            Minerva RegTech™ is a regulatory technology firm focused on strengthening compliance, financial crime prevention, and institutional integrity across global financial and legal markets.
          </p>
        </div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full opacity-5 pointer-events-none">
           <Globe size={600} className="mx-auto" />
        </div>
      </header>

      {/* Core Narrative Sections */}
      <main className="max-w-7xl mx-auto px-6 space-y-40">
        
        {/* Origin & Positioning */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
          <div className="relative group">
            <div className="bg-slate-100 dark:bg-slate-900 rounded-[4rem] aspect-square flex items-center justify-center relative overflow-hidden shadow-inner">
               <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
               <ShieldCheck size={160} className="text-blue-600 dark:text-blue-400 relative z-10 drop-shadow-2xl animate-float-slow" />
               <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-blue-600/10 rounded-full blur-[100px]"></div>
            </div>
          </div>
          <div className="space-y-10">
            <div className="space-y-4">
              <h2 className="text-xs font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.4em] flex items-center">
                <Award size={16} className="mr-3" /> The Institutional Standard
              </h2>
              <h3 className="text-5xl font-black text-slate-900 dark:text-white italic tracking-tighter">Secure. Sovereign. Intelligent.</h3>
            </div>
            <p className="text-xl text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
              We work with Fintech companies, banks, private equity and venture capital firms, stock market participants, regulators, law firms, and family offices to address complex regulatory challenges with clarity and confidence.
            </p>
            <p className="text-lg text-slate-500 dark:text-slate-400 leading-relaxed">
              Our solutions integrate advanced identity verification, anti-financial crime technologies, and efficient compliance systems designed to protect operations while supporting sustainable growth.
            </p>
            <div className="grid grid-cols-2 gap-10 pt-6">
              <AboutStat label="HQs" value="Auckland, NZ" />
              <AboutStat label="Network" value="14 Sovereign Nodes" />
              <AboutStat label="Intelligence" value="AI Neural Verification" />
              <AboutStat label="Compliance" value="AML4/5D Standard" />
            </div>
          </div>
        </section>

        {/* Strategic Pillars Grid */}
        <section className="space-y-20">
           <div className="text-center max-w-3xl mx-auto space-y-4">
              <h2 className="text-xs font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.4em]">The Architecture</h2>
              <h3 className="text-5xl font-black text-slate-900 dark:text-white italic tracking-tighter">Strategic Pillars.</h3>
           </div>
           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <PillarCard 
                icon={<Layers className="text-blue-500" />} 
                title="Unified Stack" 
                desc="Unifying identity verification, corporate intelligence, and ESG risk into a single modular portal for instant institutional oversight."
              />
              <PillarCard 
                icon={<Radar className="text-emerald-500" />} 
                title="Real-time Forensics" 
                desc="Continuous blockchain surveillance and darkweb leak detection ensuring your perimeter is defended against emerging financial crimes."
              />
              <PillarCard 
                icon={<Scale className="text-amber-500" />} 
                title="Sovereign Data" 
                desc="Regional data residency nodes across 5 continents, ensuring multijurisdictional compliance and absolute data sovereignty."
              />
           </div>
        </section>

        {/* Mission & Vision Twin Pillars */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-slate-900 rounded-[3.5rem] p-16 text-white relative overflow-hidden group shadow-2xl">
            <Target className="text-blue-400 mb-10 group-hover:scale-110 transition-transform" size={64} />
            <h3 className="text-4xl font-black mb-8 italic tracking-tighter">Our Mission</h3>
            <p className="text-slate-400 leading-relaxed font-medium text-xl">
              As regulatory expectations evolve and financial ecosystems become more interconnected, organisations require compliance frameworks that are intelligent, scalable, and defensible. Minerva RegTech™ delivers technology-driven solutions that enhance transparency, mitigate risk, and reinforce trust.
            </p>
            <div className="absolute top-0 right-0 w-80 h-80 bg-blue-600 rounded-full blur-[140px] opacity-10 translate-x-1/2 -translate-y-1/2"></div>
          </div>
          
          <div className="bg-blue-600 rounded-[3.5rem] p-16 text-white relative overflow-hidden group shadow-2xl shadow-blue-500/30">
            <Eye className="text-white/80 mb-10 group-hover:rotate-12 transition-transform" size={64} />
            <h3 className="text-4xl font-black mb-8 italic tracking-tighter">Our Vision</h3>
            <p className="text-white/80 leading-relaxed font-medium text-xl">
              To pioneer a future where regulatory compliance is a strategic advantage. We envision a global financial ecosystem where innovation and growth never compromise integrity, security, or social responsibility.
            </p>
            <div className="absolute bottom-0 right-0 w-80 h-80 bg-white rounded-full blur-[140px] opacity-20 translate-x-1/2 translate-y-1/2"></div>
          </div>
        </section>

        {/* NZ Origin Badge Footer */}
        <section className="pt-20 border-t border-slate-100 dark:border-slate-900 text-center">
          <div className="flex flex-col items-center space-y-10">
            <div className="flex items-center space-x-6 opacity-30 dark:opacity-20 grayscale">
               <div className="w-20 h-1 bg-slate-400 rounded-full"></div>
               <Globe size={32} />
               <div className="w-20 h-1 bg-slate-400 rounded-full"></div>
            </div>
            <div className="space-y-4">
              <p className="text-[12px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.6em]">Global Compliance Engineering • New Zealand Origin</p>
              <p className="text-xs text-slate-400 font-bold max-w-lg mx-auto leading-relaxed">
                Minerva RegTech is incorporated in Auckland and maintains operational nodes in London, Bangkok, New York, and Lomé.
              </p>
            </div>
            <button 
              onClick={onBack}
              className="inline-flex items-center space-x-3 bg-slate-900 dark:bg-blue-600 text-white px-12 py-6 rounded-[2rem] font-black uppercase text-xs tracking-widest hover:bg-slate-800 dark:hover:bg-blue-700 transition-all shadow-2xl active:scale-95 group"
            >
              <span>Initialize Platform</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </section>
      </main>

      <footer className="mt-40 py-12 text-center text-[10px] font-black text-slate-300 dark:text-slate-800 uppercase tracking-widest">
         © 2023 Minerva Regtech
      </footer>
    </div>
  );
};

const AboutStat: React.FC<{ label: string, value: string }> = ({ label, value }) => (
  <div className="space-y-1.5 border-l-2 border-blue-600/20 pl-6">
    <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest leading-none">{label}</p>
    <p className="text-xl font-black text-slate-900 dark:text-white tracking-tight">{value}</p>
  </div>
);

const PillarCard: React.FC<{ icon: React.ReactNode, title: string, desc: string }> = ({ icon, title, desc }) => (
  <div className="p-10 bg-slate-50 dark:bg-slate-900 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-sm group hover:bg-white dark:hover:bg-slate-950 hover:shadow-xl transition-all">
     <div className="p-5 bg-white dark:bg-slate-800 rounded-3xl w-fit mb-8 shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-all">
        {icon}
     </div>
     <h4 className="text-2xl font-black text-slate-900 dark:text-white mb-4 uppercase italic tracking-tighter">{title}</h4>
     <p className="text-slate-500 dark:text-slate-400 font-medium leading-relaxed">{desc}</p>
  </div>
);
